// Centralizes the imperative <video> + fullscreen/PiP state behind a React-friendly surface so
// the custom Controls bar can be a pure function of {state, actions}. Works for both the live
// player (seek = jump to the live edge) and VOD (seek = scrub the timeline).
import { useCallback, useEffect, useRef, useState } from "react";

const VOLUME_KEY = "maitv.volume";
const LIVE_EDGE_THRESHOLD = 4; // seconds behind the buffered edge before we flag "behind live"

export interface VideoState {
  playing: boolean;
  muted: boolean;
  volume: number; // 0..1
  isFullscreen: boolean;
  isPip: boolean;
  pipSupported: boolean;
  currentTime: number;
  duration: number; // 0 / Infinity for live
  behindLive: number; // seconds behind the live edge (live only)
  waiting: boolean;
  playbackRate: number;
}

export interface VideoActions {
  togglePlay(): void;
  toggleMute(): void;
  setVolume(v: number): void;
  seek(t: number): void;
  seekBy(delta: number): void;
  toLiveEdge(): void;
  toggleFullscreen(): void;
  togglePip(): void;
  setPlaybackRate(r: number): void;
}

export function useVideoState(
  videoRef: React.RefObject<HTMLVideoElement>,
  wrapperRef: React.RefObject<HTMLElement>,
  opts: { live: boolean },
): { state: VideoState; actions: VideoActions } {
  const storedVol = (() => {
    const v = Number(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(v) && v > 0 && v <= 1 ? v : 1;
  })();

  const [state, setState] = useState<VideoState>({
    playing: false,
    muted: true,
    volume: storedVol,
    isFullscreen: false,
    isPip: false,
    pipSupported: typeof document !== "undefined" && !!document.pictureInPictureEnabled,
    currentTime: 0,
    duration: 0,
    behindLive: 0,
    waiting: false,
    playbackRate: 1,
  });
  const patch = useCallback((p: Partial<VideoState>) => setState((s) => ({ ...s, ...p })), []);

  // Apply the stored volume once the element exists.
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.volume = storedVol;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wire media events.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => patch({ playing: true });
    const onPause = () => patch({ playing: false });
    const onVol = () => patch({ muted: v.muted, volume: v.volume });
    const onWaiting = () => patch({ waiting: true });
    const onPlaying = () => patch({ waiting: false, playing: true });
    const onRate = () => patch({ playbackRate: v.playbackRate });
    const onTime = () => {
      const buffered = v.buffered;
      const edge = buffered.length ? buffered.end(buffered.length - 1) : v.currentTime;
      patch({
        currentTime: v.currentTime,
        duration: Number.isFinite(v.duration) ? v.duration : 0,
        behindLive: opts.live ? Math.max(0, edge - v.currentTime) : 0,
      });
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("volumechange", onVol);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("ratechange", onRate);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("durationchange", onTime);
    v.addEventListener("progress", onTime);
    // Seed.
    patch({ playing: !v.paused, muted: v.muted, volume: v.volume });
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("volumechange", onVol);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("ratechange", onRate);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("durationchange", onTime);
      v.removeEventListener("progress", onTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.live]);

  // Fullscreen + PiP global listeners.
  useEffect(() => {
    const onFs = () =>
      patch({ isFullscreen: !!document.fullscreenElement && document.fullscreenElement === wrapperRef.current });
    const v = videoRef.current;
    const onPipEnter = () => patch({ isPip: true });
    const onPipLeave = () => patch({ isPip: false });
    document.addEventListener("fullscreenchange", onFs);
    v?.addEventListener("enterpictureinpicture", onPipEnter);
    v?.addEventListener("leavepictureinpicture", onPipLeave);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      v?.removeEventListener("enterpictureinpicture", onPipEnter);
      v?.removeEventListener("leavepictureinpicture", onPipLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions: VideoActions = {
    togglePlay: useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) void v.play().catch(() => {});
      else v.pause();
    }, [videoRef]),

    toggleMute: useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      const next = !v.muted;
      v.muted = next;
      if (!next && v.volume === 0) v.volume = 0.5;
      if (!next) void v.play().catch(() => {});
    }, [videoRef]),

    setVolume: useCallback(
      (vol: number) => {
        const v = videoRef.current;
        if (!v) return;
        const clamped = Math.min(1, Math.max(0, vol));
        v.volume = clamped;
        v.muted = clamped === 0;
        try {
          if (clamped > 0) localStorage.setItem(VOLUME_KEY, String(clamped));
        } catch {
          /* ignore */
        }
      },
      [videoRef],
    ),

    seek: useCallback(
      (t: number) => {
        const v = videoRef.current;
        if (v && Number.isFinite(t)) v.currentTime = t;
      },
      [videoRef],
    ),

    seekBy: useCallback(
      (delta: number) => {
        const v = videoRef.current;
        if (v && Number.isFinite(v.currentTime)) v.currentTime = Math.max(0, v.currentTime + delta);
      },
      [videoRef],
    ),

    toLiveEdge: useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      const buffered = v.buffered;
      if (buffered.length) v.currentTime = Math.max(0, buffered.end(buffered.length - 1) - 0.5);
      void v.play().catch(() => {});
    }, [videoRef]),

    toggleFullscreen: useCallback(() => {
      const wrap = wrapperRef.current;
      const v = videoRef.current;
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => {});
      } else if (wrap?.requestFullscreen) {
        void wrap.requestFullscreen().catch(() => {});
      } else if (v && "webkitEnterFullscreen" in v) {
        // iOS Safari: only the <video> can go fullscreen (no overlay/danmaku there).
        (v as unknown as { webkitEnterFullscreen(): void }).webkitEnterFullscreen();
      }
    }, [wrapperRef, videoRef]),

    togglePip: useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => {});
      else if (v.requestPictureInPicture) void v.requestPictureInPicture().catch(() => {});
    }, [videoRef]),

    setPlaybackRate: useCallback(
      (r: number) => {
        const v = videoRef.current;
        if (v) v.playbackRate = r;
      },
      [videoRef],
    ),
  };

  return { state, actions };
}
